import { useTranslation } from '@crehana/i18n';
import ArrowDown from '@crehana/icons/outline/ArrowDown';
import ArrowUp from '@crehana/icons/outline/ArrowUp';
import QuestionOutline from '@crehana/icons/QuestionOutline';
import type { IconProps } from '@crehana/icons/types';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Tooltip } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import { clsx } from 'clsx';

import localeEs from '../../i18n/localeEs';
import { WrapperCard } from './StatCard.styles';

const {
  baseLighter,
  grayMainDarkMode,
  greenMain,
  primaryLight,
  primaryLighter,
  primaryLighter12,
  redMain,
} = colors;

export interface StatCardProps {
  color?: string;
  colorDarkMode?: string;
  helpLabel?: string;
  hideVariation?: boolean;
  Icon: React.FC<IconProps>;
  iconColor?: string;
  iconColorDarkMode?: string;
  label: string;
  value: number | string;
  variation?: number;
  children?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({
  children,
  color = primaryLighter12,
  colorDarkMode = grayMainDarkMode,
  helpLabel,
  hideVariation,
  Icon,
  iconColor = primaryLight,
  iconColorDarkMode = primaryLighter,
  label,
  value,
  variation = 0,
}) => {
  const { t } = useTranslation();
  const { isDark } = useUITheme();

  const isVariationUp = variation && variation > 0;

  return (
    <WrapperCard
      isDark={isDark}
      className={`rounded-5 px-24 py-20 h-full ${!isDark && 'shadow-1-dp'}`}
    >
      {helpLabel && (
        <Tooltip
          className={`whitespace-pre-line w-160 ${
            !isDark && 'bg-base-lighter-dark-mode text-white'
          } `}
          label={helpLabel}
          position="left"
          type="regular"
        >
          <button type="button" className="absolute right-0 top-0 mr-8 mt-8">
            <QuestionOutline size={16} color={baseLighter} />
          </button>
        </Tooltip>
      )}
      <div
        className="flex items-center justify-center rounded-full w-32 h-32 mb-12"
        style={{ background: isDark ? colorDarkMode : color }}
      >
        <Icon size={24} color={isDark ? iconColorDarkMode : iconColor} />
      </div>
      <p className="font-h3">{value}</p>
      <p className="font-subtitle4 mt-4">{label}</p>
      {Boolean(variation >= 0 && !hideVariation) && (
        <p className="font-body4 mt-8 flex items-center text-gray-main">
          {isVariationUp ? (
            <ArrowUp size={16} color={greenMain} />
          ) : (
            <ArrowDown size={16} color={redMain} />
          )}
          <span
            className={clsx(
              'font-subtitle4 mr-4',
              isVariationUp ? 'text-green-main' : 'text-red-main',
            )}
          >
            {Math.abs(variation)}%
          </span>
          {t('STAT_CARD_DESCRIPTION', {
            defaultValue: localeEs.es.STAT_CARD_DESCRIPTION,
          })}
        </p>
      )}
      <div className="font-body4 mt-4 flex items-center text-gray-dark">
        {children}
      </div>
    </WrapperCard>
  );
};

export default StatCard;
